import type { Item, PlanDay } from '../contracts/items.js'

/**
 * Where a traveller is inside their own trip, right now.
 *
 * Lives in `shared` rather than in the component that renders it because it is
 * pure date arithmetic over a contract type — the same reason `zones.ts` is
 * here — and because the interesting parts are exactly the parts that want
 * testing without a browser: the calendar boundaries, and the two places where
 * doing the obvious thing is wrong.
 *
 * This can only be correct because items now carry the zone they happen in. The
 * question "what day of the trip is it" has to be answered on the clock WHERE
 * THE TRAVELLER IS, not the one their browser reports — a phone that has not
 * caught up after a flight, or a relative reading along from São Paulo, would
 * otherwise be shown the wrong day of somebody else's holiday.
 */

/** The calendar date an instant falls on, in a zone. `en-CA` gives ISO order. */
export function dateInZone(instant: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(instant)
}

export interface TripPosition {
  /** `before` the first day, `during` it, or `after` the last. */
  phase: 'before' | 'during' | 'after'
  /** Zero-based index into `days`. Null unless `during` AND the date matches one. */
  dayIndex: number | null
  /** Whole calendar days until the first day. Only meaningful when `before`. */
  daysUntil: number
  /** The next thing with a time that has not started yet. */
  next: Item | null
}

export function locateInTrip(days: PlanDay[], timeZone: string, now: Date): TripPosition {
  if (days.length === 0) {
    return { phase: 'before', dayIndex: null, daysUntil: 0, next: null }
  }

  const today = dateInZone(now, timeZone)
  const first = days[0]!.date
  const last = days[days.length - 1]!.date

  /*
   * The next thing that has not started.
   *
   * Compared as INSTANTS, unlike everything else here, because "has it happened
   * yet" is a question about a moment rather than a calendar. Days arrive in
   * order and items within a day are ordered by `starts_at`, so the first match
   * walking forwards is the answer.
   */
  let next: Item | null = null
  outer: for (const day of days) {
    for (const item of day.items) {
      if (item.startsAt && new Date(item.startsAt).getTime() > now.getTime()) {
        next = item
        break outer
      }
    }
  }

  if (today < first) {
    /*
     * Counted on the calendar, not by dividing milliseconds.
     *
     * A DST change makes one of the intervening days 23 or 25 hours long, so
     * `(then - now) / 86_400_000` rounds to the wrong number of sleeps roughly
     * twice a year — and always in the week somebody is checking most often.
     * Reducing both dates to a UTC midnight removes the offset entirely.
     */
    const [fy, fm, fd] = first.split('-').map(Number) as [number, number, number]
    const [ty, tm, td] = today.split('-').map(Number) as [number, number, number]
    const daysUntil = Math.round(
      (Date.UTC(fy, fm - 1, fd) - Date.UTC(ty, tm - 1, td)) / 86_400_000,
    )
    return { phase: 'before', dayIndex: null, daysUntil, next }
  }

  if (today > last) return { phase: 'after', dayIndex: null, daysUntil: 0, next: null }

  const dayIndex = days.findIndex((day) => day.date === today)
  return {
    phase: 'during',
    // `findIndex` returns -1 when the trip has a gap in its dates. That is
    // "inside the trip, on no particular day", which is honest — pointing at
    // day one instead would be a confident wrong answer.
    dayIndex: dayIndex === -1 ? null : dayIndex,
    daysUntil: 0,
    next,
  }
}
